import type { Socket } from "socket.io-client"

import type {
	DocPeerData,
	DocPeerLeftData,
	DocPresenceKey,
	DocPresenceState,
	DocViewer,
} from "@productlane/lib"

import { acquireWorkspaceSocket } from "@/lib/sockets/workspace-socket"
import { useDocPresenceState } from "@/lib/states/doc-presence-state"

interface Joined {
	count: number
	workspaceId: string
	viewer: DocViewer
	state: DocPresenceState
}

const joined = new Map<DocPresenceKey, Joined>()
let shared: { socket: Socket; release: () => void } | null = null

const emitJoin = (socket: Socket, docKey: DocPresenceKey, entry: Joined) => {
	socket.emit("doc:join", {
		workspaceId: entry.workspaceId,
		docKey,
		...entry.viewer,
	})
	if (entry.state.typing || entry.state.cursor) {
		socket.emit("doc:state", { docKey, ...entry.state })
	}
}

const rejoinAll = () => {
	if (!shared) return
	for (const [docKey, entry] of joined) emitJoin(shared.socket, docKey, entry)
}

// Peers seen before a drop may have left meanwhile; they announce again on rejoin.
const forgetPeers = () => useDocPresenceState.getState().clearAll()

const handlePeer = (data: DocPeerData) => {
	const entry = joined.get(data.docKey)
	if (!entry) return
	useDocPresenceState.getState().setPeer(data)
	if (data.joined) {
		shared?.socket.emit("doc:state", { docKey: data.docKey, ...entry.state })
	}
}

const handleLeft = (data: DocPeerLeftData) => {
	useDocPresenceState.getState().removePeer(data.docKey, data.socketId)
}

function connect(): Socket {
	if (shared) return shared.socket
	shared = acquireWorkspaceSocket()
	const { socket } = shared
	socket.on("connect", rejoinAll)
	socket.on("disconnect", forgetPeers)
	socket.on("doc:peer", handlePeer)
	socket.on("doc:left", handleLeft)
	return socket
}

function disconnect() {
	if (!shared) return
	const { socket, release } = shared
	shared = null
	socket.off("connect", rejoinAll)
	socket.off("disconnect", forgetPeers)
	socket.off("doc:peer", handlePeer)
	socket.off("doc:left", handleLeft)
	release()
}

/** Announces this tab in a doc until the returned function runs. */
export function joinDoc(
	docKey: DocPresenceKey,
	workspaceId: string,
	viewer: DocViewer,
): () => void {
	const existing = joined.get(docKey)
	if (existing) {
		existing.count += 1
	} else {
		const entry: Joined = { count: 1, workspaceId, viewer, state: {} }
		joined.set(docKey, entry)
		const socket = connect()
		if (socket.connected) emitJoin(socket, docKey, entry)
	}

	let left = false
	return () => {
		if (left) return
		left = true
		const entry = joined.get(docKey)
		if (!entry) return
		entry.count -= 1
		if (entry.count > 0) return
		joined.delete(docKey)
		shared?.socket.emit("doc:leave", { docKey })
		useDocPresenceState.getState().clearDoc(docKey)
		if (joined.size === 0) disconnect()
	}
}

/** Shares this tab's typing flag or caret with everyone else in the doc. */
export function updateDocState(
	docKey: DocPresenceKey,
	patch: DocPresenceState,
) {
	const entry = joined.get(docKey)
	if (!entry) return
	entry.state = { ...entry.state, ...patch }
	if (shared?.socket.connected) {
		shared.socket.emit("doc:state", { docKey, ...entry.state })
	}
}
